{/* Update input value handling to prevent null values */}
import React, { useEffect, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { TeamMember } from '../../lib/supabase-types'; // Ensure this import is correct
import ImageWithDimensions from './ImageWithDimensions';
import { toast } from 'react-hot-toast'; // Ensure correct import
import { Modal, ModalFooter, ModalButton } from '../shared/Modal';
import { supabase } from '../../lib/supabase'; // Ensure this import is correct

interface MemberEditModalProps {
  member: TeamMember | null; // Adjusted to use TeamMember
  onSave: () => Promise<void>;
  onClose: () => void;
  onChange: (member: TeamMember) => void;
  onImageBrowse: () => void; // Function to open the image gallery
  isOpen: boolean;
}

export const MemberEditModal: React.FC<MemberEditModalProps> = ({
  member,
  onSave,
  onClose,
  onChange,
  onImageBrowse,
  isOpen
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [linkedin, setLinkedin] = useState<string>('');
  const [website, setWebsite] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  // Use useEffect to set state when member prop changes
  useEffect(() => {
    if (member) {
      setImageUrl(member.image);
      setName(member.name || ''); // Default to empty string if null
      setRole(member.role || ''); // Default to empty string if null
      setEmail(member.email || ''); // Default to empty string if null
      setLinkedin(member.linkedin || ''); // Default to empty string if null
      setWebsite(member.website || ''); // Default to empty string if null
      setDescription(member.description || ''); // Default to empty string if null
    } else {
      // Reset state for adding a new member
      setImageUrl(null); // Ensure this is null for new members
      setName('');
      setRole('');
      setEmail('');
      setLinkedin('');
      setWebsite('');
      setDescription('');
    }
  }, [member]);

  const handleImageSelect = (url: string) => {
    setImageUrl(url);
  };

  const handleSave = async () => {
    if (!name || !role) {
      toast.error('Nome e função são obrigatórios');
      return;
    }

    const memberData = {
      id: member?.id || '',
      name,
      role,
      description,
      image: imageUrl || null,
      email,
      linkedin,
      website
    };

    onChange(memberData as TeamMember);

    try {
      await onSave();
      onClose();
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Erro ao salvar membro');
    }
  }

  const handleSaveDatabase = async (updatedMember: TeamMember) => {
    console.log('Database operation for member:', updatedMember); // Debugging line
    if (updatedMember.id) {
      // Update existing member
      const { data, error } = await supabase
        .from('team_members')
        .update({
          image: updatedMember.image,
          name: updatedMember.name,
          role: updatedMember.role,
          email: updatedMember.email,
          linkedin: updatedMember.linkedin,
          website: updatedMember.website,
          description: updatedMember.description,
          updated_at: new Date().toISOString(),
        })
        .eq('id', updatedMember.id);

      if (error) {
        console.error('Error updating member:', error);
        throw error;
      }
    } else {
      // Insert new member
      const { data, error } = await supabase
        .from('team_members')
        .insert({
          image: updatedMember.image,
          name: updatedMember.name,
          role: updatedMember.role,
          email: updatedMember.email,
          linkedin: updatedMember.linkedin,
          website: updatedMember.website,
          description: updatedMember.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (error) {
        console.error('Error adding member:', error);
        throw error;
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-4 bg-black text-white">
        <h2 className="text-lg font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
          {member ? 'Editar Membros' : 'Adicionar Membro'}
        </h2>
        <div className="flex flex-col mt-4">
          <label className="block text-sm font-medium mb-2 text-primary/80">Nome</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              onChange({ ...member, name: e.target.value });
            }}
            className="w-full px-4 py-3 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors"
            placeholder="Insira o nome do membro"
          />
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-primary/80">Função</label>
            <input
              type="text"
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                onChange({ ...member, role: e.target.value });
              }}
              className="w-full px-4 py-3 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors"
              placeholder="Insira a função"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-primary/80">Biografia</label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                onChange({ ...member, description: e.target.value });
              }}
              className="w-full px-4 py-3 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors"
              placeholder="Insira uma biografia do membro"
              rows={4}
            />
          </div>
          <label className="block text-sm font-medium mb-2 text-primary/80">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              onChange({ ...member, email: e.target.value });
            }}
            className="w-full px-4 py-3 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors"
            placeholder="Insira o email do membro"
          />
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2 text-primary/80">Linkedin</label>
            <input
              type="url"
              value={linkedin}
              onChange={(e) => {
                setLinkedin(e.target.value);
                onChange({ ...member, linkedin: e.target.value });
              }}
              className="w-full px-4 py-3 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors"
              placeholder="Insira a URL do LinkedIn"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-primary/80">Website</label>
            <input
              type="url"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
                onChange({ ...member, website: e.target.value });
              }}
              className="w-full px-4 py-3 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors"
              placeholder="Insira a URL do site"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1 text-primary/80">Url da Imagen</label>
          <input
            type="text"
            value={imageUrl || ''}
            onChange={(e) => setImageUrl(e.target.value)}
            className="w-full px-4 py-3 bg-secondary/50 rounded-lg focus:ring-2 focus:ring-primary focus:outline-none border border-primary/10 hover:border-primary/30 transition-colors"
            placeholder="Insira a URL da imagem"
          />
          <div className="flex items-center mt-2">
            <button
              type="button"
              onClick={onImageBrowse}
              className="bg-blue-500 text-white rounded px-6 py-3 hover:bg-blue-600 transition duration-300 transform hover:scale-105"
            >
              Procurar
            </button>
          </div>
          {imageUrl && <ImageWithDimensions src={imageUrl} alt="Member Image" />}
        </div>
      </div>
      <ModalFooter>
        <ModalButton className="bg-blue-500 hover:bg-blue-600 text-white" onClick={handleSave}>Salvar</ModalButton>
        <ModalButton className="bg-gray-700 text-white" onClick={onClose}>Cancelar</ModalButton>
      </ModalFooter>
    </Modal>
  );
};

export default MemberEditModal;